import React, { useState, useEffect } from "react";
import Base from "../../Config/Base";
import { API_BASE_URL } from "../../Config/Config";
import "../../Config/loader.css";
import Pagination from "../../Config/Pagination";
import { startOfWeek, isMonday, format, addDays } from "date-fns";
import { useLocation } from "react-router-dom";
import { Footer } from "../footer/footer";

export default function WeeklySalesReport() {
  const location = useLocation();
  const { reportId } = location.state || {};

  const [fromDate, setFromDate] = useState("");
  const [selectedStore, setSelectedStore] = useState("0");
  const [storesData, setStoresData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  const [userDetails, setUserDetails] = useState(null);

  useEffect(() => {
    const user = JSON.parse(
      sessionStorage.getItem("MisUserDetails") ||
        localStorage.getItem("MisUserDetails") ||
        "{}",
    );
    setUserDetails(user);
  }, []);

  // Default to current week's Monday
  useEffect(() => {
    const today = new Date();
    const monday = startOfWeek(today, { weekStartsOn: 1 });
    setFromDate(format(monday, "yyyy-MM-dd"));
  }, []);

  // Load stores (same as GST report)
  useEffect(() => {
    if (!userDetails?.OrgId || !userDetails?.UserId) return;

    const fetchStores = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}Settings/getUserStores?OrgId=${userDetails.OrgId}&UserId=${userDetails.UserId}`,
        );
        const data = await res.json();
        if (data?.ResultData) {
          setStoresData(data.ResultData);
        }
      } catch (err) {
        console.error("Failed to load stores:", err);
      }
    };

    fetchStores();
  }, [userDetails]);

  const fetchWeeklySales = () => {
    if (!userDetails?.OrgId || !fromDate) return;

    setLoading(true);

    const payload = {
      OrgId: userDetails.OrgId,
      UserId: userDetails.UserId,
      ReportId: reportId,
      ReportCriteria: {
        FromDate: fromDate,
        ToDate: format(addDays(new Date(fromDate), 6), "yyyy-MM-dd"),
        StoreId: selectedStore === "0" ? 0 : Number(selectedStore),
        ProductId: 0,
        CategoryId: 0,
        CustomerId: 0,
        SupplierId: 0,
        UserId: 0,
        LoginUserId: userDetails.UserId,
        OrgId: userDetails.OrgId,
        City: 0,
        Status: 0,
      },
    };

    fetch(`${API_BASE_URL}report/getreport`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.data) {
          setSalesData(data.data);
        } else {
          setSalesData([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setSalesData([]);
        setLoading(false);
      });
  };

  // Auto-fetch on mount with defaults
  useEffect(() => {
    if (userDetails?.OrgId && fromDate) {
      fetchWeeklySales();
    }
  }, [userDetails, fromDate]);

  const handleDateChange = (e) => {
    const selected = e.target.value;
    if (isMonday(new Date(selected))) {
      setFromDate(selected);
    } else {
      alert("Please select only Monday dates.");
    }
  };

  const handleSearch = () => {
    fetchWeeklySales();
  };

  const itemsPerPage = 10;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = salesData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return format(date, "dd-MM-yyyy");
  };

  return (
    <Base>
      <div>
        <div className="mb-2">
          <div className="page-title mb-2">
            <h4>Weekly Sales Report</h4>
            <h6>Sales summary for selected week</h6>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            {/* If you want search bar later, add it here like GST report */}
            <div className="search-bar col-10" style={{ visibility: "hidden" }}>
              {/* Placeholder to keep layout same */}
            </div>

            <button
              onClick={() => setIsFilterVisible(!isFilterVisible)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <i
                className="fa fa-filter"
                style={{ fontSize: "24px", color: "#333" }}
              ></i>
            </button>
          </div>
        </div>

        {isFilterVisible && (
          <div className="row g-3 mb-3 mb-0">
            {/* Store Dropdown */}
            <div className="col-6 col-lg-3">
              <label className="form-label">Store</label>
              <select
                className="form-select"
                value={selectedStore}
                onChange={(e) => setSelectedStore(e.target.value)}
              >
                <option value="0">-- All --</option>
                {storesData.map((store) => (
                  <option key={store.StoreId} value={store.StoreId}>
                    {store.Name}
                  </option>
                ))}
              </select>
            </div>

            {/* Start Date - Monday only */}
            <div className="col-6 col-lg-3">
              <label className="form-label"> Start On</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={handleDateChange}
              />
              <small className="text-muted d-block mt-1">
                Only Monday dates allowed
              </small>
            </div>

            {/* Search Button - aligned like GST report */}
            <div
              className="col-12 col-lg-3 mb-0"
              style={{ marginTop: "-15px" }}
            >
              <button
                className="btn btn-secondary w-100 mt-4"
                onClick={handleSearch}
                disabled={loading || !fromDate}
              >
                <i className="fa-solid fa-magnifying-glass"></i> Search
              </button>
            </div>
          </div>
        )}

        <div className="card-container mb-5">
          {loading ? (
            <div className="text-center my-5">
              <div className="containers m-auto d-flex justify-content-center align-items-center">
                <div className="dot bg-primary"></div>
                <div className="dot bg-secondary"></div>
                <div className="dot bg-success"></div>
                <div className="dot bg-danger"></div>
                <div className="dot bg-warning"></div>
                <div className="dot bg-info"></div>
              </div>
              <p>Loading weekly sales...</p>
            </div>
          ) : salesData.length === 0 ? (
            <div className="col-12 text-center py-5 text-muted">
              No sales found for the selected week and store.
            </div>
          ) : (
            <div className="row mb-2">
              {currentItems.map((item, index) => {
                const mergedDateTime = item.OrderDate
                  ? `${format(new Date(item.OrderDate), "dd-MM-yyyy")} ${item.OrderTime || "00:00:00"}`
                  : "—";

                // Truncate product name for single-line display
                const truncatedName =
                  item.ProductName && item.ProductName.length > 22
                    ? item.ProductName.substring(0, 22) + "..."
                    : item.ProductName || "—";

                return (
                  <div key={index} className="col-md-6">
                    <div
                      className="store-card card shadow-sm my-1"
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        padding: "1px",
                      }}
                    >
                      <div className="card-body pb-3 pt-3">
                        {/* Row 2: Product Name (left) + Unit Price & Total (right) */}
                        <div className="row mb-1" style={{ color: "#6c757d" }}>
                          {/* Left: Product Name (truncated) */}
                          <div className="col-7 col-sm-8">
                            <span
                              className="d-flex align-items-center text-truncate"
                              style={{
                                fontWeight: "500",
                                color: "#333",
                                maxWidth: "100%",
                              }}
                              title={item.ProductName} // full name on hover
                            >
                              <i
                                className="fa-solid fa-box me-2"
                                style={{ color: "#007bff", minWidth: "20px" }}
                              ></i>
                              {truncatedName}
                            </span>
                          </div>

                          {/* Right: Unit Price + Total */}
                          <div className="col-5 col-sm-4 text-end">
                            <div className="d-flex flex-column align-items-end">
                              {/* Unit Price */}
                              <span
                                className="mb-1"
                                style={{ fontSize: "0.95rem" }}
                              >
                                <i
                                  className="fa-solid fa-rupee-sign me-1"
                                  style={{ color: "#007bff", minWidth: "16px" }}
                                ></i>
                                {item.UnitPrice || 0}/-
                              </span>

                              {/* Total - prominent */}
                            </div>
                          </div>
                        </div>

                        {/* Row 1: Date & Time + Order No */}
                        <div className="row mb-2" style={{ color: "#6c757d" }}>
                          <div className="col-6">
                            <span className="d-flex align-items-center">
                              <i
                                className="fa-solid fa-calendar-days me-2"
                                style={{ color: "#007bff", minWidth: "20px" }}
                              ></i>
                              {mergedDateTime}
                            </span>
                          </div>
                          <div className="col-6 text-end">
                            <span className="d-flex align-items-center">
                              <i
                                className="fa-solid fa-receipt me-2"
                                style={{ color: "#007bff", minWidth: "20px" }}
                              ></i>
                              {item.OrderNo || "—"}
                            </span>
                          </div>
                        </div>

                        {/* Row 3: Quantity (now alone at bottom left) */}
                        <div className="row" style={{ color: "#6c757d" }}>
                          <div className="col-6">
                            <span className="d-flex align-items-center">
                              <i
                                className="fa-solid fa-cubes me-2"
                                style={{ color: "#007bff", minWidth: "20px" }}
                              ></i>
                              Qty: {item.Quantity || 0}
                            </span>
                          </div>
                          <div className="col-6 text-end">
                            <span
                              style={{
                                fontSize: "1.05rem",
                                fontWeight: "bold",
                                color: "#28a745",
                              }}
                            >
                              <i
                                className="fa-solid fa-money-bill-wave me-1"
                                style={{ color: "#28a745", minWidth: "16px" }}
                              ></i>
                              {item.OrderdetailTotal || 0}/-
                            </span>
                          </div>
                        </div>

                        {/* Optional Store name */}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination - same as before */}
          <Pagination
            itemsPerPage={10}
            totalItems={salesData.length}
            paginate={paginate}
            currentPage={currentPage}
          />
        </div>
      </div>

      <br />
      <br />
      <br />
      <Footer />
    </Base>
  );
}
